import React, { useEffect, useState } from 'react';
import { supabase } from '../services/supabase';
import { logActivity } from '../lib/activity';
import { useProfile } from '../hooks/useProfile';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  navy:     '#0A1628',
  gold:     '#C9A84C',
  black:    '#0A0A0A',
  white:    '#FFFFFF',
  surface:  '#F5F6F8',
  border:   '#D0D5DD',
  borderLt: '#E8EBF0',
  text:     '#0A0A0A',
  textSec:  '#3D4552',
  muted:    '#6B7280',
  mutedLt:  '#9CA3AF',
  success:  '#0A7C42',
  danger:   '#C0222A',
};

const stages = [
  { key: 'intro_meeting_scheduled', label: 'Intro Meeting', color: '#0D6E8A', bg: '#EFF8FB', border: '#0D6E8A' },
  { key: 'idd',                     label: 'IDD',           color: '#92640A', bg: '#FDF6E7', border: '#92640A' },
  { key: 'odd',                     label: 'ODD',           color: '#1D4ED8', bg: '#EFF4FF', border: '#1D4ED8' },
  { key: 'ddq_sent',                label: 'DDQ Sent',      color: '#6D28D9', bg: '#F5F3FF', border: '#6D28D9' },
  { key: 'terms_negotiation',       label: 'Terms',         color: '#B45309', bg: '#FFFBEB', border: '#B45309' },
  { key: 'invested',                label: 'Invested',      color: '#0A7C42', bg: '#F0FDF4', border: '#0A7C42' },
  { key: 'declined',                label: 'Declined',      color: '#C0222A', bg: '#FEF2F2', border: '#C0222A' },
  { key: 'on_hold',                 label: 'On Hold',       color: '#6B7280', bg: '#F9FAFB', border: '#D0D5DD' },
];

interface Connection {
  id: string;
  status: string;
  allocator_firm: { id: string; name: string };
  manager_firm:   { id: string; name: string };
  fund:           { id: string; name: string };
  current_stage:  string | null;
}

export default function Pipeline() {
  const { firm } = useProfile();
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading]         = useState(true);
  const [updating, setUpdating]       = useState<string | null>(null);
  const [selected, setSelected]       = useState<Connection | null>(null);

  useEffect(() => { if (firm) fetchPipeline(); }, [firm]);

  const fetchPipeline = async () => {
    if (!firm) return;
    const { data } = await supabase
      .from('connections')
      .select(`id, status,
        allocator_firm:firms!connections_allocator_firm_id_fkey(id, name),
        manager_firm:firms!connections_manager_firm_id_fkey(id, name),
        fund:funds(id, name)`)
      .eq('status', 'approved')
      .or(`allocator_firm_id.eq.${firm.id},manager_firm_id.eq.${firm.id}`);

    if (data) {
      const withStages = await Promise.all(data.map(async conn => {
        const { data: stageData } = await supabase
          .from('pipeline_stages').select('stage')
          .eq('connection_id', conn.id)
          .order('created_at', { ascending: false }).limit(1).single();
        return { ...conn, current_stage: stageData?.stage || null };
      }));
      setConnections(withStages);
    }
    setLoading(false);
  };

  const updateStage = async (connectionId: string, stage: string) => {
    if (!firm) return;
    setUpdating(connectionId);
    const uid = (await supabase.auth.getUser()).data.user?.id;
    await supabase.from('pipeline_stages').insert({
      connection_id: connectionId, stage,
      updated_by: uid,
    });
    if (uid) logActivity(uid, firm.id, 'pipeline_stage_updated', 'connection', connectionId, {
      stage,
      manager_firm_name: selected?.manager_firm?.name,
      allocator_firm_name: selected?.allocator_firm?.name,
    });
    await fetchPipeline();
    setUpdating(null);
    setSelected(null);
  };

  const getOtherFirmName = (conn: Connection) =>
    conn.allocator_firm?.id === firm?.id ? conn.manager_firm?.name : conn.allocator_firm?.name;

  const getStageInfo = (key: string | null) =>
    stages.find(s => s.key === key) || { label: 'Unassigned', color: C.muted, bg: C.surface, border: C.border, key: '' };

  // Group by stage
  const grouped: Record<string, Connection[]> = {};
  stages.forEach(s => { grouped[s.key] = []; });
  grouped['none'] = [];
  connections.forEach(conn => {
    const key = conn.current_stage || 'none';
    if (grouped[key] !== undefined) grouped[key].push(conn);
    else grouped['none'].push(conn);
  });

  if (loading) return (
    <div style={{ padding: '32px', background: C.surface, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: C.muted, fontSize: '13px', fontFamily: 'system-ui' }}>Loading pipeline...</div>
    </div>
  );

  const totalActive = connections.length;
  const invested    = connections.filter(c => c.current_stage === 'invested').length;
  const inProgress  = connections.filter(c => c.current_stage && c.current_stage !== 'declined' && c.current_stage !== 'on_hold' && c.current_stage !== 'invested').length;

  return (
    <div style={{ padding: '28px 32px', background: C.surface, minHeight: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      {/* ── Header ── */}
      <div style={{ marginBottom: '22px', paddingBottom: '18px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h1 style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: '24px', color: C.black, fontWeight: 400, margin: '0 0 4px', letterSpacing: '-0.3px' }}>
            Pipeline
          </h1>
          <p style={{ fontSize: '13px', color: C.muted, margin: 0 }}>
            Track all investor relationships and deal stages
          </p>
        </div>
        {/* Summary stats */}
        <div style={{ display: 'flex', gap: '20px' }}>
          {[
            { label: 'Total Active', value: totalActive },
            { label: 'In Progress',  value: inProgress },
            { label: 'Invested',     value: invested },
          ].map(({ label, value }) => (
            <div key={label} style={{ textAlign: 'right' }}>
              <div style={{ fontFamily: '"Courier New", monospace', fontSize: '20px', fontWeight: 700, color: C.navy }}>{value}</div>
              <div style={{ fontSize: '10px', color: C.muted, fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      {connections.length === 0 ? (
        <div style={{
          background: C.white, border: `1px solid ${C.border}`,
          borderRadius: '4px', padding: '60px', textAlign: 'center',
        }}>
          <div style={{ fontSize: '14px', color: C.text, marginBottom: '6px', fontWeight: 500 }}>No connections yet</div>
          <div style={{ fontSize: '12px', color: C.muted }}>Approved connections appear here for pipeline tracking</div>
        </div>
      ) : (
        <>
          {/* ── Kanban board ── */}
          <div style={{ overflowX: 'auto', paddingBottom: '8px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, minmax(160px, 1fr))', gap: '10px', minWidth: '1280px' }}>
              {stages.map(stage => {
                const cards = [...(grouped[stage.key] || [])];
                if (stage.key === 'intro_meeting_scheduled') {
                  cards.push(...(grouped['none'] || []));
                }
                return (
                  <div key={stage.key}>
                    {/* Column header */}
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      marginBottom: '8px', padding: '6px 8px',
                      background: stage.bg,
                      border: `1px solid ${stage.border}20`,
                      borderRadius: '3px',
                    }}>
                      <span style={{
                        fontSize: '10px', fontWeight: 700, color: stage.color,
                        textTransform: 'uppercase', letterSpacing: '0.6px',
                      }}>
                        {stage.label}
                      </span>
                      <span style={{
                        fontSize: '10px', fontWeight: 700,
                        background: stage.color + '18',
                        color: stage.color,
                        padding: '1px 6px', borderRadius: '2px',
                        fontFamily: '"Courier New", monospace',
                      }}>
                        {cards.length}
                      </span>
                    </div>

                    {/* Cards */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', minHeight: '80px' }}>
                      {cards.map(conn => {
                        const isUnassigned = !conn.current_stage;
                        return (
                          <div
                            key={conn.id}
                            onClick={() => setSelected(conn)}
                            style={{
                              background: C.white,
                              border: `1px solid ${C.border}`,
                              borderLeft: `3px solid ${isUnassigned ? C.borderLt : stage.color}`,
                              borderRadius: '3px', padding: '10px',
                              cursor: 'pointer', transition: 'box-shadow 0.1s',
                            }}
                            onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)')}
                            onMouseLeave={e => (e.currentTarget.style.boxShadow = 'none')}
                          >
                            <div style={{ fontSize: '12px', fontWeight: 600, color: C.text, marginBottom: '3px', lineHeight: 1.3 }}>
                              {getOtherFirmName(conn)}
                            </div>
                            {conn.fund?.name && (
                              <div style={{ fontSize: '10.5px', color: C.muted, lineHeight: 1.3 }}>
                                {conn.fund.name}
                              </div>
                            )}
                            {isUnassigned && (
                              <div style={{ fontSize: '9px', color: C.mutedLt, marginTop: '4px', fontWeight: 600, letterSpacing: '0.4px', textTransform: 'uppercase' }}>
                                Unassigned
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── List view below kanban ── */}
          <div style={{ marginTop: '28px' }}>
            <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '10px' }}>
              All Connections
            </div>
            <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '4px', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: C.navy }}>
                    {['Counterparty', 'Fund', 'Stage', 'Action'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '8px 14px', fontSize: '10px', color: 'rgba(255,255,255,0.65)', fontWeight: 600, letterSpacing: '0.5px' }}>
                        {h.toUpperCase()}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {connections.map((conn, i) => {
                    const si = getStageInfo(conn.current_stage);
                    return (
                      <tr key={conn.id}
                        style={{ borderBottom: `1px solid ${C.borderLt}`, background: i % 2 === 0 ? C.white : C.surface }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#EBF0FA')}
                        onMouseLeave={e => (e.currentTarget.style.background = i % 2 === 0 ? C.white : C.surface)}
                      >
                        <td style={{ padding: '10px 14px', fontSize: '12.5px', fontWeight: 600, color: C.text }}>
                          {getOtherFirmName(conn)}
                        </td>
                        <td style={{ padding: '10px 14px', fontSize: '12px', color: C.textSec }}>
                          {conn.fund?.name || '-'}
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{
                            fontSize: '10px', fontWeight: 700,
                            padding: '3px 8px', borderRadius: '2px',
                            background: si.bg, color: si.color,
                            textTransform: 'uppercase', letterSpacing: '0.5px',
                          }}>
                            {si.label}
                          </span>
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <button
                            onClick={() => setSelected(conn)}
                            style={{
                              padding: '4px 12px', borderRadius: '3px',
                              fontSize: '11px', fontWeight: 500,
                              cursor: 'pointer', fontFamily: 'system-ui',
                              border: `1px solid ${C.border}`,
                              background: C.white, color: C.textSec,
                              letterSpacing: '0.2px',
                            }}
                          >
                            Update Stage
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ── Stage update modal ── */}
      {selected && (
        <div
          onClick={() => setSelected(null)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(10,22,40,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: C.white,
              border: `1px solid ${C.border}`,
              borderRadius: '4px', padding: '28px',
              width: '440px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            }}
          >
            {/* Modal header */}
            <div style={{ borderBottom: `1px solid ${C.border}`, paddingBottom: '14px', marginBottom: '18px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: C.muted, letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '4px' }}>
                Update Pipeline Stage
              </div>
              <div style={{ fontSize: '15px', fontWeight: 600, color: C.navy, fontFamily: 'Georgia, serif' }}>
                {getOtherFirmName(selected)}
              </div>
              {selected.fund?.name && (
                <div style={{ fontSize: '12px', color: C.muted, marginTop: '2px' }}>
                  {selected.fund.name}
                </div>
              )}
            </div>

            {/* Stage buttons */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '16px' }}>
              {stages.map(stage => {
                const isCurrent = selected.current_stage === stage.key;
                return (
                  <button
                    key={stage.key}
                    onClick={() => updateStage(selected.id, stage.key)}
                    disabled={updating === selected.id}
                    style={{
                      padding: '9px 12px', borderRadius: '3px',
                      fontSize: '12px', fontWeight: isCurrent ? 700 : 400,
                      cursor: 'pointer', textAlign: 'left',
                      border: `1px solid ${isCurrent ? stage.color : C.border}`,
                      background: isCurrent ? stage.bg : C.white,
                      color: isCurrent ? stage.color : C.textSec,
                      fontFamily: 'system-ui',
                      opacity: updating === selected.id ? 0.5 : 1,
                      letterSpacing: '0.2px',
                      transition: 'all 0.1s',
                    }}
                    onMouseEnter={e => {
                      if (!isCurrent) {
                        (e.currentTarget as HTMLButtonElement).style.borderColor = stage.color;
                        (e.currentTarget as HTMLButtonElement).style.background = stage.bg;
                        (e.currentTarget as HTMLButtonElement).style.color = stage.color;
                      }
                    }}
                    onMouseLeave={e => {
                      if (!isCurrent) {
                        (e.currentTarget as HTMLButtonElement).style.borderColor = C.border;
                        (e.currentTarget as HTMLButtonElement).style.background = C.white;
                        (e.currentTarget as HTMLButtonElement).style.color = C.textSec;
                      }
                    }}
                  >
                    {isCurrent && '✓ '}{stage.label}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setSelected(null)}
              style={{
                width: '100%',
                background: C.white, border: `1px solid ${C.border}`,
                borderRadius: '3px', color: C.muted,
                fontSize: '12px', padding: '8px',
                cursor: 'pointer', fontFamily: 'system-ui',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}